import { Fragment } from 'react';
import { cn } from '@shared/lib/cn';
import { Icon } from '@shared/ui/icon/Icon';

export interface StepperProps {
  steps: readonly string[];
  /** Index de l'étape courante (0-based). */
  current: number;
}

/**
 * Indicateur de progression du parcours d'ajout.
 * Étapes passées (coche), courante (mise en évidence) et à venir.
 */
export function Stepper({ steps, current }: StepperProps) {
  return (
    <nav aria-label="Progression" className="w-full">
      <ol className="flex items-center">
        {steps.map((label, index) => {
          const done = index < current;
          const active = index === current;
          return (
            <Fragment key={label}>
              <li aria-current={active ? 'step' : undefined} className="flex items-center gap-2">
                <span
                  className={cn(
                    'flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors',
                    done && 'bg-emerald-600 text-white',
                    active && 'bg-emerald-600 text-white ring-4 ring-emerald-100',
                    !done && !active && 'border border-slate-300 bg-white text-slate-400',
                  )}
                >
                  {done ? <Icon name="check" size={14} /> : index + 1}
                </span>
                <span
                  className={cn(
                    'text-sm whitespace-nowrap',
                    active ? 'font-semibold text-slate-900' : 'text-slate-500',
                    // Sur petit écran, n'afficher le libellé que de l'étape courante.
                    !active && 'hidden sm:inline',
                  )}
                >
                  {label}
                </span>
              </li>
              {index < steps.length - 1 ? (
                <li aria-hidden="true" className="mx-2 flex-1 sm:mx-3">
                  <span
                    className={cn(
                      'block h-px',
                      index < current ? 'bg-emerald-500' : 'bg-slate-200',
                    )}
                  />
                </li>
              ) : null}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
